import { Globe } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';

export const TIMEZONE_OPTIONS = [
  { value: 'America/Los_Angeles', label: '(UTC-08:00) Los Angeles - EUA' },
  { value: 'America/New_York', label: '(UTC-05:00) Nova York - EUA' },
  { value: 'America/Sao_Paulo', label: '(UTC-03:00) São Paulo - Brasil' },
  { value: 'America/Buenos_Aires', label: '(UTC-03:00) Buenos Aires - Argentina' },
  { value: 'Europe/London', label: '(UTC±00:00) Londres - Reino Unido' },
  { value: 'Europe/Lisbon', label: '(UTC±00:00) Lisboa - Portugal' },
  { value: 'Europe/Berlin', label: '(UTC+01:00) Berlim - Alemanha' },
  { value: 'Asia/Kolkata', label: '(UTC+05:30) Nova Delhi - Índia' },
  { value: 'Asia/Singapore', label: '(UTC+08:00) Singapura' },
  { value: 'Asia/Tokyo', label: '(UTC+09:00) Tóquio - Japão' },
] as const;

export type TimezoneValue = typeof TIMEZONE_OPTIONS[number]['value'];

/** Map of UTC offset in minutes for each timezone option */
const TIMEZONE_OFFSETS: Record<string, number> = {
  'America/Los_Angeles': -480,
  'America/New_York': -300,
  'America/Sao_Paulo': -180,
  'America/Buenos_Aires': -180,
  'Europe/London': 0,
  'Europe/Lisbon': 0,
  'Europe/Berlin': 60,
  'Asia/Kolkata': 330,
  'Asia/Singapore': 480,
  'Asia/Tokyo': 540,
};

/** Detect the best matching timezone from the simplified list */
export function detectTimezone(): string {
  try {
    const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    // Exact match
    if (TIMEZONE_OPTIONS.some(tz => tz.value === browserTz)) {
      return browserTz;
    }
    // Match by offset
    const browserOffset = -new Date().getTimezoneOffset(); // in minutes, positive = east
    let closest = 'America/Sao_Paulo';
    let minDiff = Infinity;
    for (const [tz, offset] of Object.entries(TIMEZONE_OFFSETS)) {
      const diff = Math.abs(browserOffset - offset);
      if (diff < minDiff) {
        minDiff = diff;
        closest = tz;
      }
    }
    return closest;
  } catch {
    return 'America/Sao_Paulo';
  }
}

interface TimezoneSelectProps {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  disabled?: boolean;
  className?: string;
}

export default function TimezoneSelect({
  value,
  onChange,
  label = 'Fuso Horário',
  disabled = false,
  className,
}: TimezoneSelectProps) {
  const selectedTz = TIMEZONE_OPTIONS.find(tz => tz.value === value);

  return (
    <div className={className}>
      {label && (
        <Label className="flex items-center gap-2 mb-2">
          <Globe className="w-4 h-4 text-muted-foreground" />
          {label}
        </Label>
      )}
      <Select value={value} onValueChange={onChange} disabled={disabled}>
        <SelectTrigger className="w-full">
          <SelectValue placeholder="Selecione seu fuso horário">
            {selectedTz?.label || value}
          </SelectValue>
        </SelectTrigger>
        <SelectContent className="z-50 bg-popover">
          {TIMEZONE_OPTIONS.map((tz) => (
            <SelectItem key={tz.value} value={tz.value}>
              {tz.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
